import { useCallback } from "react";
import { IdPErrorCode } from "./constants";
import { IdPTokens, LoginWithIdPOptions } from "./types";

export interface AccessTokenSilentlyOrLoginOptions {
  fromUnifiedAccountSwitching?: boolean;
}

/**
 * Returns a function that gets an access token without prompting, and falls back to an
 * interactive login when the IdP answers `login_required`. Any other failure resolves to
 * `undefined`.
 *
 * `login` is passed in rather than read from context so the caller decides what a login also
 * clears (the unified provider keeps the SendGrid session when coming from account switching).
 */
export function useAccessTokenSilentlyOrLogin(
  getTokensSilently: () => Promise<IdPTokens>,
  login: (opts: LoginWithIdPOptions) => Promise<void>
): (opts: AccessTokenSilentlyOrLoginOptions) => Promise<string | undefined> {
  return useCallback(
    async ({ fromUnifiedAccountSwitching = false }: AccessTokenSilentlyOrLoginOptions) => {
      try {
        const { access_token } = await getTokensSilently();

        return access_token;
      } catch (error: any) {
        if (error?.error === IdPErrorCode.LOGIN_REQUIRED) {
          await login({ fromUnifiedAccountSwitching });
        }
      }
    },
    [getTokensSilently, login]
  );
}
